import {NgModule} from '@angular/core';
import {RouterModule, Routes} from '@angular/router';

import {LayoutComponent} from './shared/layout/layout.component';
import {ComingSoonPageComponent} from './pages/others/coming-soon/coming-soon.component';
import {IndexComponent} from './pages/home/index/index.component';
import {EditorialComponent} from './pages/editorial/index/editorial.component';
import {VideoComponent} from './pages/video/index/video.component';
import {AboutComponent} from './pages/about/about.component';
import {ContactPageComponent} from './pages/contact/contact.component';
import {PlanComponent} from './pages/plan/plan.component';
import {ContributorProfileComponent} from './pages/contributor-profile/contributor-profile.component';
import {WishlistComponent} from './pages/wishlist/wishlist.component';
import {SuccessComponent} from './pages/success/success.component';
import {AuthGuard} from './auth.guard';
import {AiEnquiryComponent} from './pages/ai-enquiry/ai-enquiry.component';
import {AiComponent} from './pages/ai/index/ai.component';
import { LicenseRequestVerificationComponent } from './pages/ai/license-request-verification/license-request-verification.component';
import { HomeComponent as ExclusiveHomeComponent } from './pages/exclusive/home/home.component';
import { AiChatComponent } from './pages/ai-chat/ai-chat.component';
import { EnterpriseComponent } from './pages/enterprise/enterprise.component';
import { OffersComponent } from './pages/offers/offers.component';
import { AuthCallbackComponent } from './pages/others/auth-callback/auth-callback.component';

const routes: Routes = [
    {
        path: 'pages/coming-soon',
        component: ComingSoonPageComponent
    },
    {
        path: 'license-request-verification',
        pathMatch: 'full',
        component: LicenseRequestVerificationComponent
    },
    /*
     * Landing route for the Google sign-in redirect, and for its failures.
     * A sibling of the LayoutComponent route because it renders no page of its
     * own -- it reads the session out of the query string and navigates away.
     */
    {
        path: 'auth/callback',
        pathMatch: 'full',
        component: AuthCallbackComponent
    },
    // Mahatta Image Studio. Deliberately a sibling of the LayoutComponent route
    // rather than a child: the component carries its own topbar, user menu and
    // left nav, and owns the full viewport height while scrolling internally —
    // the same arrangement the dashboard route gets via hideHeader/fullHeight.
    {
        path: 'ai',
        pathMatch: 'full',
        component: AiChatComponent
    },
    {
        path: '',
        component: LayoutComponent,
        children: [
            {
                path: '',
                pathMatch: 'full',
                component: IndexComponent
            },
            {
                path: 'editorial',
                pathMatch: 'full',
                component: EditorialComponent
            },
            {
                path: 'videos',
                pathMatch: 'full',
                component: VideoComponent
            },
            {
                path: 'about',
                pathMatch: 'full',
                component: AboutComponent
            },
            {
                path: 'contact',
                pathMatch: 'full',
                component: ContactPageComponent
            },
            {
                path: 'enterprise',
                pathMatch: 'full',
                component: EnterpriseComponent
            },
            {
                path: 'subscription',
                pathMatch: 'full',
                component: PlanComponent
            },
            {
                path: 'offers',
                pathMatch: 'full',
                component: OffersComponent
            },
            {
                path: 'contributor',
                loadChildren: () => import( './pages/contributor-profile/contributor-profile.module' ).then(m => m.ContributorProfileModule)
            },
            {
                path: 'ai-enquiry',
                pathMatch: 'full',
                component: AiEnquiryComponent
            },
            // {
            //     path: 'ai',
            //     pathMatch: 'full',
            //     component: AiComponent
            // },
            // 'ai' is NOT here: the studio is a standalone full-screen workspace
            // with its own topbar and left nav, so it sits outside LayoutComponent
            // (see the top-level route) instead of under the site header/footer.
            {
                path: 'success',
                pathMatch: 'full',
                component: SuccessComponent
            },
            /*
             * Where a failed payment lands.
             *
             * The gateway callbacks send the customer to FRONT_URL + '/failed'
             * (Cashfree::…, Helper::…), but no route claimed it, so every failed
             * payment fell through the '**' wildcard to /pages/404 — a Not Found
             * page for a transaction that was very much found and declined.
             *
             * Straight back to /subscription rather than a page of its own: the
             * only useful thing to do after a declined payment is to try again,
             * and that is where the plans are. The gateway shows the customer why
             * it failed before it redirects here.
             *
             * FRONT_URL carries a trailing slash, so the URL that actually
             * arrives is '//failed'. Angular's serializer collapses it to
             * '/failed', so this route catches both.
             */
            {
                path: 'failed',
                pathMatch: 'full',
                redirectTo: '/subscription'
            },
            {
                path: 'wishlist',
                pathMatch: 'full',
                component: WishlistComponent
            , canActivate: [AuthGuard]
            },
            {
                path: 'lightbox-detail',
                loadChildren: () => import('./pages/wishlist-detail/wishlist-detail.module').then(m => m.WishlistDetailModule),
                data: {breadcrumb: 'Wishlist'}, canActivate: [AuthGuard]
            },
            {
                path: 'lightbox',
                loadChildren: () => import('./pages/wishlist-detail/wishlist-detail.module').then(m => m.WishlistDetailModule),
                data: {breadcrumb: 'Wishlist'}, canActivate: [AuthGuard]
            },
            {
                path: 'policy',
                loadChildren: () => import( './pages/policy/policy.module' ).then(m => m.PolicyModule)
            },
            {
                path: 'product',
                loadChildren: () => import( './pages/products/products.module' ).then(m => m.ProductsModule)
            },
            // {
            //     path: 'elements',
            //     loadChildren: () => import( './pages/elements/elements.module' ).then(m => m.ElementsModule)
            // },
            // {
            //     path: 'blog',
            //     loadChildren: () => import( './pages/blog/blog.module' ).then(m => m.BlogModule)
            // },
            {
                path: 'pages',
                loadChildren: () => import( './pages/others/pages.module' ).then(m => m.PagesModule)
            },
            {
                path: 'shop',
                loadChildren: () => import( './pages/shop/shop.module' ).then(m => m.ShopModule)
                , canActivate: [AuthGuard]
            },
            // {
            //     path: 'product',
            //     loadChildren: () => import( './pages/product/product.module' ).then(m => m.ProductModule)
            // },
            {
                path: 'account',
                loadChildren: () => import( './pages/dashboard/dashboard.module' ).then(m => m.DashboardModule)
            },
            /*
             * Where the reset link from the forgot-password mail lands.
             *
             * ChangePasswordModule and its component both existed but were never
             * routed to, so /change-password/<token> fell through to the '**'
             * wildcard and redirected to /pages/404 — every reset mail led to a
             * Not Found page.
             *
             * A child of LayoutComponent, not a sibling: it is an ordinary page
             * someone reads and fills in, so it should carry the site header and
             * footer. (auth/callback is a sibling because it renders nothing and
             * navigates away immediately — different case.) The module's own
             * routes supply both '' and ':token'.
             */
            {
                path: 'change-password',
                loadChildren: () => import('./pages/change-password/change-password.module').then(m => m.ChangePasswordModule)
            },
        ]
    },
    {
        path: 'exclusive/account',
        loadChildren: () => import( './pages/exclusive/dashboard/dashboard.module' ).then(m => m.DashboardModule)
    },
    {
        path: 'exclusive',
        pathMatch: 'full',
        component: ExclusiveHomeComponent
    },
    {
        path: '**',
        redirectTo: '/pages/404'
    }
];

@NgModule({
    imports: [RouterModule.forRoot(routes, {useHash: false, anchorScrolling: 'disabled', scrollPositionRestoration: 'disabled'})],
    exports: [RouterModule]
})

export class AppRoutingModule {
}
